import React, { useState } from 'react';
import { kycService } from '../../services/kycService';
import HMSPartnerSearch from '../ui/HMSPartnerSearch';

/**
 * Invite-a-partner modal: creates the KYC case + portal account and shows the
 * secure invite link (always copyable — email delivery is best-effort).
 */
const KYCInviteModal = ({ onClose, onCreated }) => {
    const currentYear = new Date().getFullYear();
    const [companyName, setCompanyName] = useState('');
    const [selectedProfile, setSelectedProfile] = useState(null); // linked CRM partner
    const [manualEntry, setManualEntry] = useState(false); // type a net-new company name
    const [contactName, setContactName] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [year, setYear] = useState(currentYear);
    const [formType, setFormType] = useState('STANDARD');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null); // { invite_link, email_sent }
    const [copied, setCopied] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        const effectiveName = (selectedProfile?.legal_entity_name || companyName).trim();
        if (!effectiveName || !contactEmail.trim()) {
            setError('Company name and contact email are required.');
            return;
        }
        setSubmitting(true);
        try {
            const data = await kycService.createCase({
                company_name: effectiveName,
                contact_name: contactName.trim(),
                contact_email: contactEmail.trim(),
                year: Number(year),
                form_type: formType,
                // Explicit link when a CRM partner was picked — avoids the
                // exact-legal-name auto-match (which silently leaves the case
                // unlinked when the names differ).
                ...(selectedProfile?.id ? { crm_profile_id: selectedProfile.id } : {}),
            });
            setResult(data);
            if (onCreated) onCreated(data.case);
        } catch (err) {
            setError(err.response?.data?.error || 'Could not create the KYC case. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(result.invite_link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            // Clipboard API unavailable — the link stays selectable in the input.
        }
    };

    return (
        <div className="kyc-modal-overlay" onClick={onClose}>
            <div className="kyc-modal" onClick={(event) => event.stopPropagation()}>
                {!result ? (
                    <>
                        <h2>Invite Partner to KYC</h2>
                        <p className="kyc-modal-sub">
                            The partner contact receives a secure link to set their password,
                            upload the required documents and complete the onboarding form.
                        </p>
                        {error && <div className="kyc-form-error">{error}</div>}
                        <form onSubmit={handleSubmit}>
                            <div className="kyc-field">
                                <label>Company Name <span className="kyc-required">*</span></label>
                                {manualEntry ? (
                                    <>
                                        <input
                                            type="text"
                                            value={companyName}
                                            onChange={(event) => setCompanyName(event.target.value)}
                                            placeholder="e.g. Acme Shipping & Logistics GmbH"
                                            autoFocus
                                        />
                                        <button
                                            type="button"
                                            className="kyc-inline-toggle"
                                            onClick={() => { setManualEntry(false); setCompanyName(''); }}
                                        >
                                            ← Search existing CRM partners instead
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <HMSPartnerSearch
                                            value={selectedProfile}
                                            onChange={(profile) => {
                                                setSelectedProfile(profile);
                                                setCompanyName(profile?.legal_entity_name || '');
                                            }}
                                            placeholder="Search CRM partner by legal name…"
                                            autoFocus
                                        />
                                        <button
                                            type="button"
                                            className="kyc-inline-toggle"
                                            onClick={() => { setSelectedProfile(null); setManualEntry(true); }}
                                        >
                                            Partner not in CRM yet? Enter a name manually
                                        </button>
                                    </>
                                )}
                                {selectedProfile && (
                                    <p className="kyc-modal-hint">
                                        Linked to CRM profile — the case will attach to “{selectedProfile.legal_entity_name}”.
                                    </p>
                                )}
                            </div>
                            <div className="kyc-field">
                                <label>Contact Person</label>
                                <input
                                    type="text"
                                    value={contactName}
                                    onChange={(event) => setContactName(event.target.value)}
                                    placeholder="e.g. Hans Mueller"
                                />
                            </div>
                            <div className="kyc-field">
                                <label>Contact Email <span className="kyc-required">*</span></label>
                                <input
                                    type="email"
                                    value={contactEmail}
                                    onChange={(event) => setContactEmail(event.target.value)}
                                    placeholder="e.g. hans.mueller@acme-shipping.com"
                                />
                            </div>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <div className="kyc-field" style={{ flex: 1 }}>
                                    <label>KYC Year</label>
                                    <select value={year} onChange={(event) => setYear(event.target.value)}>
                                        {[currentYear - 1, currentYear, currentYear + 1].map((option) => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="kyc-field" style={{ flex: 1 }}>
                                    <label>Form Type</label>
                                    <select value={formType} onChange={(event) => setFormType(event.target.value)}>
                                        <option value="STANDARD">Standard</option>
                                        <option value="SA">KYC South Africa</option>
                                    </select>
                                </div>
                            </div>
                            <div className="kyc-modal-actions">
                                <button type="button" className="kyc-btn-secondary" onClick={onClose}>
                                    Cancel
                                </button>
                                <button type="submit" className="kyc-btn-primary" disabled={submitting}>
                                    {submitting ? 'Creating…' : 'Create Case & Send Invite'}
                                </button>
                            </div>
                        </form>
                    </>
                ) : (
                    <>
                        <h2>Invite Created</h2>
                        <p className="kyc-modal-sub">
                            KYC case for <strong>{companyName}</strong> ({year}) is ready.
                        </p>
                        {result.email_sent ? (
                            <div className="kyc-form-success">
                                Invite email sent to {contactEmail}.
                            </div>
                        ) : (
                            <div className="kyc-form-error">
                                The invite email could not be sent automatically — please share
                                the link below with the partner directly.
                            </div>
                        )}
                        <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>
                            Secure invite link (valid 14 days)
                        </label>
                        <div className="kyc-invite-link-box">
                            <input type="text" readOnly value={result.invite_link} onFocus={(event) => event.target.select()} />
                            <button type="button" className="kyc-doc-download-btn" onClick={copyLink}>
                                {copied ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                        <div className="kyc-modal-actions">
                            <button type="button" className="kyc-btn-primary" onClick={onClose}>
                                Done
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default KYCInviteModal;
